import { z } from 'zod';

/**
 * Role model — the code-side mirror of ADR-004.
 *
 * Promotion reason: authorization is cross-cutting by definition (§7.3), and
 * every feature consults it. It lives in `core/` as pure policy with no
 * platform access.
 *
 * [ABSOLUTE] These predicates are a *convenience for the UI*, not the security
 * boundary. The client is untrusted (§7.1). Firestore rules and Cloud Functions
 * enforce the same decisions server-side, and disagreeing with this file does
 * not grant access. Never treat a `true` here as authorization.
 */

export const roleSchema = z.enum([
  /** An employee of the school. Own record, own dependents, own declarations. */
  'employee',
  /** Committee: enrolment, employee and dependent administration. */
  'committee_hr',
  /** Committee: costs, claims, payments, aggregate reporting. */
  'committee_finance',
  /** Committee: final review and approval authority. */
  'committee_manager',
]);

export type Role = z.infer<typeof roleSchema>;

/**
 * Roles are additive, not hierarchical — one person may hold several. A manager
 * is not automatically finance; the school decides who holds what.
 */
export const rolesSchema = z
  .array(roleSchema)
  .min(1, 'يجب تحديد دور واحد على الأقل')
  .max(4)
  .refine((roles) => new Set(roles).size === roles.length, 'الأدوار مكررة');

export const COMMITTEE_ROLES: readonly Role[] = [
  'committee_hr',
  'committee_finance',
  'committee_manager',
];

export function hasRole(roles: readonly Role[], role: Role): boolean {
  return roles.includes(role);
}

export function isCommitteeMember(roles: readonly Role[]): boolean {
  return roles.some((role) => COMMITTEE_ROLES.includes(role));
}

/** Reading another employee's record at all requires committee membership. */
export function canReadOtherEmployees(roles: readonly Role[]): boolean {
  return isCommitteeMember(roles);
}

/**
 * Medical detail is the most sensitive data the fund holds. Finance settles
 * claims from amounts and does not need the diagnosis behind them, so it is
 * excluded here — least privilege (§7.6), not an oversight.
 */
export function canReadMedicalDetail(roles: readonly Role[]): boolean {
  return hasRole(roles, 'committee_hr') || hasRole(roles, 'committee_manager');
}

/** Only the manager finalises a declaration. */
export function canApproveDeclaration(roles: readonly Role[]): boolean {
  return hasRole(roles, 'committee_manager');
}

export function canManageEmployees(roles: readonly Role[]): boolean {
  return hasRole(roles, 'committee_hr') || hasRole(roles, 'committee_manager');
}

export function canManageClaims(roles: readonly Role[]): boolean {
  return hasRole(roles, 'committee_finance') || hasRole(roles, 'committee_manager');
}

/** Editing fund policy changes the rules for everyone; manager only. */
export function canEditFundPolicy(roles: readonly Role[]): boolean {
  return hasRole(roles, 'committee_manager');
}

/**
 * Role assignment is itself privileged (ADR-004) and is audited. Enforced in a
 * Cloud Function; a client can never write its own roles.
 */
export function canAssignRoles(roles: readonly Role[]): boolean {
  return hasRole(roles, 'committee_manager');
}
